<script>
/**
 * Concept Quicktable
 * 
 * @Version 1.0
 * @Author Daniel Lepe
 */
import { captions, baseIndex as fetchingAPI } from './usuarioArchetype.js'

import _ from 'lodash'
import axios from 'axios'
import QuickCardFooter from '@/views/elements/quick-card-footer.vue'
import automodal from './usuarioModal.vue'

let name = 'usuarioQuicktable',
	newButtonLabel = 'New',
	emptyHeader = 'Data not found',
	emptyLead = 'Module empty',
	reloadData = 'Try reloading this page',
	tryAgainLater = '',
	header = null,
	footer = null;

const sumarios = {
	adeudo: 0,
	adeudo_promedio: 0,
	monedero: 0,
	monedero_promedio: 0,
}

export default {
	name,

	components: { automodal, QuickCardFooter },

	props: {
		fetchingAPI: {
			type: String,
			default: () => { return fetchingAPI }
		},
		header: {
			type: String,
			default: () => { return header }
		},
		perPage: {
			type: Number,
			default: 10,
		},
		footer: {
			type: String,
			default: () => { return footer }
		},
		captions: {
			type: [Array, Object],
			default: () => captions
		}
	},

	computed: {
		sourceURI() {
			const navigationConfig = this.perPage + "/" + this.currentPage;
			let init = null;
			switch (this.$route.name) {
				case 'Usuarios':
					init = '/usuarios/prefilter/' + this.$route.params.prefilter + '/' + navigationConfig;
					break;
				case 'Restaurantes':
					init = '/usuarios/prefilter/restaurantes/' + navigationConfig;
					break;
				case 'Administradores':
					init = '/usuarios/prefilter/admins/' + navigationConfig;
					break;
				default:
					init = this.fetchingAPI + navigationConfig;
					break;
			}
			if (this.term) {
				init += "/" + this.term;
			} else if (!this.term || this.term == "s") {
				init += "/@";
			}
			return init;
		}
	},

	data: () => {
		return {
			name, newButtonLabel, emptyHeader,
			emptyLead, tryAgainLater, reloadData,
			currentPage: 1,
			items: [],
			itemsCount: 0,
			term: null,
			isLoading: false,
			sumarios: _.clone(sumarios)
		}
	},

	methods: {
		async refreshFromStart() {
			this.currentPage = 1
			this.refresh()
		},
		async refresh() {
			this.isLoading = true
			await this.loadUp()
			this.isLoading = false
		},
		async loadUp() {
			try {
				let response = await axios.get(this.sourceURI)
				this.items = response.data.rows;
				this.itemsCount = response.data.count;
				if(response.data.sumarios)
					this.sumarios = response.data.sumarios;
				else 
					this.sumarios = _.clone(sumarios)
				this.loading = false;
			} catch (error) {
				console.error(error)
			}
		}
	},

	watch: {
		currentPage(a, b) {
			this.refresh();
		},
		'$route'() {
			this.currentPage = 1;
			this.loadUp();
		}
	},

	mounted() {
		this.refresh()
	}
};
</script>

<template>
	<div :id="name">
		<b-card no-body>

			<!-- TABLE CONTENT -->
			<b-table v-if="items.length && !isLoading" class="animated fadeIn" responsive :items="items" :fields="captions">

				<template #cell(monedero)="data">
					<strong :class="{ minus: (data.value < 0), plus: (data.value > 0) }">
						{{ data.value | currency }}
					</strong>
				</template>

				<template #cell(costo)="data">
					{{ data.value | currency }}
				</template>

				<template #cell(imagen)="data">
					<img-portal :src="data.value" allow-zoom />
				</template>

				<template #cell(_)="data">
					<automodal :concept-id="data.item.id" @refresh="refresh">
						Ver
					</automodal>
				</template>

			</b-table>

			<!-- LOADING SPINNER -->

			<div v-if="isLoading">
				<div class="text-center p-5">
					<b-spinner variant="primary" />
				</div>
			</div>

			<!-- EMPTY JUMBOTRON -->

			<div v-else-if="!items.length" class="pt-4 pl-4 pr-4 pb-0">
				<b-jumbotron :header="emptyHeader" :lead="emptyLead">
					<p>{{ tryAgainLater }}</p>
					<b-button variant="primary" @click="refresh()">{{ reloadData }}</b-button>
				</b-jumbotron>
			</div>

			<!-- HEADER -->

			<template v-slot:header>
				<b-row align-h="between">

					<b-col md="auto" v-if="!!header">
						<h6 class="mt-2 mb-2">{{ header }}</h6>
					</b-col>

					<b-col>
						<b-input-group>
							<b-form-input v-model="term" @keyup.enter="refreshFromStart()" placeholder="Buscador..." />
							<b-input-group-append>
								<b-button v-if="!!term" variant="secondary" @click="term = ''; refreshFromStart();"><i
										class="fa fa-times"></i></b-button>
								<b-button variant="secondary" @click="refreshFromStart()" :disabled="!term"><i
										class="fa fa-search"></i></b-button>
							</b-input-group-append>
						</b-input-group>
					</b-col>

					<b-col md="auto" class="align-right" v-if="$route.meta.mode == 'Normal'">
						<b-button-group class="mr-3">
							<b-button :to="{ name: $route.name, params: { prefilter: 'todos' } }" variant="secondary" :active="$route.params.prefilter=='todos'">
								Todos
							</b-button>
							<b-button :to="{ name: $route.name, params: { prefilter: 'vip' } }" variant="secondary" :active="$route.params.prefilter=='vip'">
								VIP
							</b-button>
						</b-button-group>

						<b-button-group class="mr-3">
							<b-button :to="{ name: $route.name, params: { prefilter: 'pedidos-activos' } }" variant="secondary">
								Pedidos activos
							</b-button>
							<b-button :to="{ name: $route.name, params: { prefilter: 'psinrevisar' } }" variant="secondary">
								Pagos sin revisar
							</b-button>
						</b-button-group>

						<b-button-group>
							<b-button :to="{ name: $route.name, params: { prefilter: 'adeudo' } }" variant="secondary">
								Adeudo
							</b-button>
						</b-button-group>
					</b-col>

				</b-row>
			</template>

			<!-- FOOTER -->

			<template v-slot:footer v-if="!isLoading && items.length">
				<quick-card-footer
					v-model="currentPage" 
					:items-count="itemsCount" 
					:per-page="perPage" 
					:items-fetched="items.length"
					@refresh="refresh">
					<template #sumarios>
						<div class="text-muted text-right" v-if="sumarios">
							<div class="block">
								<strong class="minus">{{ sumarios.adeudo | currency }}</strong> 
								<i title="Adeudo" v-b-tooltip.hover class="fa fa-fw fa-info-circle"></i>
							</div>
							<div class="block">
								<strong :class="{ minus: (sumarios.monedero < 0), plus: (sumarios.monedero > 0) }">{{ sumarios.monedero | currency }}</strong> 
								<i title="Monedero" v-b-tooltip.hover class="fa fa-fw fa-info-circle"></i>
							</div>
						</div>
					</template>
				</quick-card-footer>
			</template>

	</b-card>
</div>
</template>

<style lang="less">
	#usuarioQuicktable {
		.minus {
			color: var(--danger);
		}
		.plus {
			color: var(--primary);
		}
	}
</style>